import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/logo-inline.svg';

function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  // Scroll effect logic
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Function to handle navigation to home sections
  const navigateToSection = (sectionId) => {
    if (isHomePage) {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = `/#${sectionId}`;
    }
    setMobileMenuOpen(false); // Close mobile menu after clicking
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-gold-gradient-135 shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img src={logo} alt="AuLedger Logo" className="h-8 w-auto mr-2" />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center">
          <nav className="mr-8">
            <ul className="flex space-x-8">
              <li>
                <button
                  onClick={() => navigateToSection('features')}
                  className="text-black hover:text-white"
                >
                  Features
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateToSection('pricing')}
                  className="text-black hover:text-white"
                >
                  Pricing
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateToSection('support')}
                  className="text-black hover:text-white"
                >
                  Support
                </button>
              </li>
            </ul>
          </nav>

          {/* Desktop Download Button */}
          <a
            href="https://apps.apple.com/us/app/auledger/id6742725527"
            target="_blank"
            rel="noopener noreferrer"
            className={`hidden md:inline-flex items-center px-4 py-2 rounded-lg bg-black text-white hover:bg-gray-800 transition-all ${
              scrolled ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
            }`}
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.33-3.14-2.57C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
            </svg>
            Download
          </a>
        </div>

        {/* Mobile Download Button + Hamburger Menu Wrapper */}
        <div className="flex items-center md:hidden">
          {/* Mobile Download Button (Left of Hamburger Menu) */}
          {scrolled && (
            <a
              href="https://apps.apple.com/us/app/auledger/id6742725527"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-2 rounded-lg bg-black text-white hover:bg-gray-800 transition-all mr-4"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.33-3.14-2.57C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              Download
            </a>
          )}

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-black focus:outline-none"
          >
            ☰
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="absolute left-0 top-full w-full bg-white shadow-lg rounded-b-lg md:hidden">
          <ul className="py-2 px-4 space-y-2">
            <li>
              <button
                onClick={() => navigateToSection('features')}
                className="block w-full text-left py-2 px-4 text-black hover:bg-gray-200 rounded"
              >
                Features
              </button>
            </li>
            <li>
              <button
                onClick={() => navigateToSection('pricing')}
                className="block w-full text-left py-2 px-4 text-black hover:bg-gray-200 rounded"
              >
                Pricing
              </button>
            </li>
            <li>
              <button
                onClick={() => navigateToSection('support')}
                className="block w-full text-left py-2 px-4 text-black hover:bg-gray-200 rounded"
              >
                Support
              </button>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}

export default Header;
